import React from 'react'
import { Popover, Box, Typography, Divider } from '@mui/material'
import type { PlatinumData } from '../../types/carbon/models/PlatinumData'
import { formatCellValue } from './styles'


/**
 * Provenance information for a clicked cell
 * Shows the hierarchy path for both top and side axes
 */
export interface CellProvenanceInfo {
  topHierarchy: Array<{
    type: 'group' | 'label' | 'letter';
    text: string;
  }>;
  sideHierarchy: Array<{
    type: 'group' | 'label' | 'letter' | 'band';
    text: string;
  }>;
  cellData: {
    freq?: number;
    colpc?: number;
    rowpc?: number;
    sig?: string | null;
  };
  bandType: string;
  vecType?: string;
}

interface CellInfoPopoverProps {
  open: boolean;
  position: { top: number; left: number } | null;
  provenance: CellProvenanceInfo | null;
  properties?: PlatinumData['properties'];
  clickedCellRect: DOMRect | null;
  onClose: () => void;
}

export const CellInfoPopover: React.FC<CellInfoPopoverProps> = ({
  open,
  position,
  provenance,
  properties,
  clickedCellRect,
  onClose
}) => {
  if (!provenance || !position || !clickedCellRect) return null

  const formatValue = (value: number | undefined, band: string): string => {
    if (value === undefined) return '—'

    const sourceData = {
      freq: band === 'freq' ? value : undefined,
      colpc: band === 'colpc' ? value : undefined,
      rowpc: band === 'rowpc' ? value : undefined,
    }

    return formatCellValue(sourceData, band, properties || {}, provenance.vecType)
  }

  // Popover dimensions (approximate)
  const POPOVER_WIDTH = 600
  const POPOVER_HEIGHT = 300

  // Calculate available space
  const spaceRight = window.innerWidth - clickedCellRect.right
  const spaceBottom = window.innerHeight - clickedCellRect.bottom

  // Determine if we should flip the layout
  const shouldFlip = spaceRight < POPOVER_WIDTH || spaceBottom < POPOVER_HEIGHT

  // Calculate anchor position based on flip state
  let anchorPosition: { top: number; left: number }
  let transformOriginVertical: 'top' | 'bottom'
  let transformOriginHorizontal: 'left' | 'right'

  if (shouldFlip) {
    // Flipped: position popover so clicked cell appears in bottom-right quadrant
    anchorPosition = {
      top: clickedCellRect.bottom,
      left: clickedCellRect.right
    }
    transformOriginVertical = 'bottom'
    transformOriginHorizontal = 'right'
  } else {
    // Default: position popover so clicked cell appears in top-left quadrant
    anchorPosition = {
      top: clickedCellRect.top,
      left: clickedCellRect.left
    }
    transformOriginVertical = 'top'
    transformOriginHorizontal = 'left'
  }

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: transformOriginVertical,
        horizontal: transformOriginHorizontal,
      }}
      PaperProps={{
        sx: {
          maxWidth: 600,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible'
        }
      }}
    >
      <Box sx={{ p: 0 }}>
        {/* 2x2 Grid Layout */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: 'auto auto',
          gap: 1
        }}>
          {shouldFlip ? (
            // Flipped layout: Values in top-left, row in top-right, column in bottom-left, empty in bottom-right
            <>
              {/* Top-left cell: Values */}
              <Box sx={{
                backgroundColor: 'white',
                boxShadow: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2
              }}>
                <Typography variant="overline" sx={{ fontWeight: 600, color: '#666' }}>
                  Values
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {provenance.cellData.freq !== undefined && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#666' }}>Freq:&nbsp;</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {formatValue(provenance.cellData.freq, 'freq')}
                      </Typography>
                    </Box>
                  )}
                  {provenance.cellData.colpc !== undefined && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#666' }}>Col %:&nbsp;</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#0066CC' }}>
                        {formatValue(provenance.cellData.colpc, 'colpc')}
                      </Typography>
                    </Box>
                  )}
                  {provenance.cellData.rowpc !== undefined && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#666' }}>Row %:&nbsp;</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#CC0000' }}>
                        {formatValue(provenance.cellData.rowpc, 'rowpc')}
                      </Typography>
                    </Box>
                  )}
                  {provenance.cellData.sig && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#666' }}>Sig:&nbsp;</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#f57c00' }}>
                        {provenance.cellData.sig}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Top-right cell: Column */}
              <Box sx={{
                backgroundColor: 'white',
                boxShadow: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2
              }}>
                <Box>
                  {provenance.topHierarchy.map((item, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      sx={{
                        ml: idx * 2,
                        color: item.type === 'letter' ? '#999' : '#333',
                        display: 'block',
                      }}
                    >
                      {item.type === 'letter' ? `[${item.text}]` : item.text}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {/* Bottom-left cell: Row */}
              <Box sx={{
                backgroundColor: 'white',
                boxShadow: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2
              }}>
                <Box>
                  {provenance.sideHierarchy.map((item, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      sx={{
                        ml: idx * 2,
                        mb: 1,
                        color: item.type === 'letter' || item.type === 'band' ? '#999' : '#333',
                        display: 'block',
                      }}
                    >
                      {item.type === 'letter' ? `[${item.text}]` :
                       item.type === 'band' ? `(${item.text})` :
                       item.text}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {/* Bottom-right cell: Empty (fully transparent) */}
              <Box sx={{ minHeight: 60 }} />
            </>
          ) : (
            // Default layout: Empty in top-left, column in top-right, row in bottom-left, values in bottom-right
            <>
              {/* Top-left cell: Empty (fully transparent) */}
              <Box sx={{ minHeight: 60 }} />

              {/* Top-right cell: Column */}
              <Box sx={{
                backgroundColor: 'white',
                boxShadow: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2
              }}>
                <Box>
                  {provenance.topHierarchy.map((item, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      sx={{
                        ml: idx * 2,
                        color: item.type === 'letter' ? '#999' : '#333',
                        display: 'block',
                      }}
                    >
                      {item.type === 'letter' ? `[${item.text}]` : item.text}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {/* Bottom-left cell: Row */}
              <Box sx={{
                backgroundColor: 'white',
                boxShadow: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2
              }}>
                <Box>
                  {provenance.sideHierarchy.map((item, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      sx={{
                        ml: idx * 2,
                        mb: 1,
                        color: item.type === 'letter' || item.type === 'band' ? '#999' : '#333',
                        display: 'block',
                      }}
                    >
                      {item.type === 'letter' ? `[${item.text}]` :
                       item.type === 'band' ? `(${item.text})` :
                       item.text}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {/* Bottom-right cell: Values */}
              <Box sx={{
                backgroundColor: 'white',
                boxShadow: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2
              }}>
                <Typography variant="overline" sx={{ fontWeight: 600, color: '#666' }}>
                  Values
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {provenance.cellData.freq !== undefined && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#666' }}>Freq:&nbsp;</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {formatValue(provenance.cellData.freq, 'freq')}
                      </Typography>
                    </Box>
                  )}
                  {provenance.cellData.colpc !== undefined && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#666' }}>Col %:&nbsp;</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#0066CC' }}>
                        {formatValue(provenance.cellData.colpc, 'colpc')}
                      </Typography>
                    </Box>
                  )}
                  {provenance.cellData.rowpc !== undefined && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#666' }}>Row %:&nbsp;</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#CC0000' }}>
                        {formatValue(provenance.cellData.rowpc, 'rowpc')}
                      </Typography>
                    </Box>
                  )}
                  {provenance.cellData.sig && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#666' }}>Sig:&nbsp;</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#f57c00' }}>
                        {provenance.cellData.sig}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Popover>
  )
}

export default CellInfoPopover
